import { useState, useEffect, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { TextField } from "@/components/TextField";
import { SubmitButton } from "@/components/SubmitButton";
import { PasskeysCard } from "@/components/PasskeysCard";
import { PasswordCard } from "@/components/PasswordCard";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/auth/AuthProvider";
import {
  associateTotp,
  verifyTotp,
  disableTotp,
  getMfaStatus,
  type TotpAssociation,
} from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

import { buttonClass } from "@/components/buttonStyles";
// Account-security settings. The first factor offered is an authenticator app
// (TOTP), pairing with the PMS BFF endpoints (PMS-S039/S046). The browser never
// sees a Cognito secret — only the per-user TOTP seed, which the broker's app
// stores. On mount it reads GET /api/broker/mfa (PMS-S046) so the page reflects
// whether TOTP is already on — across reloads, not just the current session.

type Phase =
  | { kind: "loading" }
  | { kind: "idle" }
  | { kind: "enrolling"; association: TotpAssociation }
  | { kind: "enabled" };

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

// Group the secret into 4-char chunks so it's readable for manual entry.
function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

export function SecurityPage() {
  const { authedRequest, posture, refreshPosture } = useAuth();
  const hasPassword = posture?.hasPassword ?? true;
  const hasPasskey = posture?.hasPasskey ?? (posture?.passkeyCount ?? 0) > 0;
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Bumped when MFA is enabled so the passkeys card re-fetches (the server
  // deletes passkeys when MFA turns on — PMS-S052).
  const [reloadPasskeys, setReloadPasskeys] = useState(0);

  // Reflect current MFA status on load (BRK-S016 / PMS-S046). A failed status
  // read degrades to the actionable CTA + a note, never a broken/blank card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { totp } = await getMfaStatus(authedRequest);
        if (!cancelled) setPhase({ kind: totp ? "enabled" : "idle" });
      } catch {
        if (!cancelled) {
          setPhase({ kind: "idle" });
          setError("Couldn’t load your current MFA status.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  async function onSetUp() {
    setError(null);
    setPending(true);
    try {
      const association = await associateTotp(authedRequest);
      setPhase({ kind: "enrolling", association });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await verifyTotp(authedRequest, code);
      setPhase({ kind: "enabled" });
      setCode("");
      // Enabling MFA deletes any passkeys server-side (PMS-S052) — refresh card.
      setReloadPasskeys((n) => n + 1);
      // Posture changed (a strong factor now exists) — re-read it so the
      // onboarding gate stops prompting (BRK-S026).
      void refreshPosture();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  // Passkeys and MFA are mutually exclusive (PMS-S052). When the passkeys card
  // changes, the server may have just switched MFA off — re-read so this card
  // reflects it. Don't clobber an in-progress enrollment.
  async function refreshMfa() {
    try {
      const { totp } = await getMfaStatus(authedRequest);
      setPhase((prev) =>
        prev.kind === "enrolling" ? prev : { kind: totp ? "enabled" : "idle" },
      );
    } catch {
      // Keep the current view on a transient status read failure.
    }
    // A passkey add/remove also changes the strong-factor posture — re-read it.
    void refreshPosture();
  }

  async function onDisable() {
    setError(null);
    setPending(true);
    try {
      await disableTotp(authedRequest);
      setPhase({ kind: "idle" });
      void refreshPosture();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  // The authenticator (TOTP) is the live source for the MFA factor; posture
  // covers password + passkeys. "Secure" mirrors the onboarding bar: a passkey,
  // or a password paired with MFA (BRK-S026). Only claimed once posture is known.
  const mfaOn = phase.kind === "enabled";
  const secure = posture != null && (hasPasskey || (hasPassword && mfaOn));

  return (
    <>
      <PageHeader
        title="Account security"
        meta="A passkey is the strongest way to sign in. You can also use a password with an authenticator app — you use a passkey or an authenticator, not both."
      />
      <section className="flex flex-col gap-6">
        {secure && (
          <div className="flex flex-col gap-1 rounded-lg border border-green-600/30 bg-green-50 dark:bg-green-400/10 p-5">
            <p className="font-semibold text-green-800 dark:text-green-200">
              ✓ Your account is secure
            </p>
            <p className="text-sm text-green-800/80 dark:text-green-200/80">
              {hasPasskey
                ? "You’re signed in with a passkey — the strongest, phishing-resistant option."
                : "You have a password and an authenticator app. For the strongest protection, consider adding a passkey."}
            </p>
          </div>
        )}

        <PasskeysCard
          mfaActive={phase.kind === "enabled"}
          onChange={refreshMfa}
          reloadToken={reloadPasskeys}
          recommended={!hasPasskey}
        />

        <PasswordCard
          request={authedRequest}
          hasPassword={hasPassword}
          onChanged={refreshPosture}
        />

        <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
          <div className="flex flex-col gap-1">
            <h2 className="text-heading">Authenticator app</h2>
            <p className="text-sm text-brk-dim">
              Use an app like Google Authenticator, 1Password, or Authy to
              generate a 6-digit code at sign-in.
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}

          {phase.kind === "loading" && (
            <p role="status" className="text-sm text-brk-dim">
              Checking your security settings…
            </p>
          )}

          {phase.kind === "idle" && !hasPassword && (
            <p className="text-sm text-brk-dim">
              An authenticator app is a second factor, so you’ll need a password
              first — set one above. A passkey, by contrast, needs no password.
            </p>
          )}

          {phase.kind === "idle" && hasPassword && hasPasskey && (
            <p className="text-sm text-brk-dim">
              You already have a passkey, which is more secure than a password
              and an authenticator app. Setting up an authenticator app will
              turn your passkey off — most brokers don’t need this.
            </p>
          )}

          {phase.kind === "idle" &&
            // When a passkey is already protecting the account we discourage MFA:
            // the option stays available but rendered as a quiet, secondary action.
            (hasPasskey ? (
              <button
                type="button"
                onClick={onSetUp}
                disabled={pending || !hasPassword}
                className={`${buttonClass({ variant: "secondary" })} self-start`}
              >
                {pending ? "Starting…" : "Set up anyway"}
              </button>
            ) : (
              <button
                type="button"
                onClick={onSetUp}
                disabled={pending || !hasPassword}
                className={`${buttonClass()} self-start`}
              >
                {pending ? "Starting…" : "Set up authenticator app"}
              </button>
            ))}

          {phase.kind === "enrolling" && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-brk-dim">
                Scan this QR code with your authenticator app, then enter the
                6-digit code it shows to finish.
              </p>
              {/* White in both themes: a QR code must stay dark-on-light to
                scan (BRK-S245; nightVariants.test.ts allows it). */}
              <div className="self-center rounded-md bg-white p-3">
                <QRCodeSVG
                  value={phase.association.otpauthUri}
                  size={160}
                  aria-label="Authenticator QR code"
                />
              </div>
              <p className="text-center text-sm text-brk-dim">
                Can’t scan? Enter this key manually:
                <br />
                <code
                  data-testid="totp-secret"
                  className="mt-1 inline-block break-all tabular-nums text-brk-text"
                >
                  {formatSecret(phase.association.secretCode)}
                </code>
              </p>
              <form className="flex flex-col gap-4" onSubmit={onVerify}>
                <TextField
                  label="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                <p className="text-xs text-brk-dim">
                  Codes refresh every 30 seconds. If one is rejected, enter the
                  next one — and make sure your authenticator app’s clock is
                  synced.
                </p>
                <SubmitButton pending={pending} pendingLabel="Verifying…">
                  Verify and enable
                </SubmitButton>
              </form>
              <button
                type="button"
                onClick={() => {
                  setPhase({ kind: "idle" });
                  setError(null);
                  setCode("");
                }}
                className="self-start text-sm text-brk-accent hover:underline"
              >
                Cancel
              </button>
            </div>
          )}

          {phase.kind === "enabled" && (
            <div className="flex flex-col gap-3">
              <p
                role="status"
                className="text-sm font-medium text-green-700 dark:text-green-300"
              >
                ✓ Authenticator app is on. You’ll be asked for a code next time
                you sign in.
              </p>
              <button
                type="button"
                onClick={onDisable}
                disabled={pending}
                className={`${buttonClass({ variant: "secondary" })} self-start`}
              >
                {pending ? "Turning off…" : "Turn off"}
              </button>
            </div>
          )}
        </div>

        <Link to="/portal" className="text-sm text-brk-accent hover:underline">
          ← Back to portal
        </Link>
      </section>
    </>
  );
}
